<script setup>
import { computed, provide, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
import UiCard from '@/components/ui/UiCard.vue';
import SettingsLayout from '@/components/settings/SettingsLayout.vue';
import IdentitySection from '@/components/school-settings/IdentitySection.vue';
import DepedSection from '@/components/school-settings/DepedSection.vue';
import EmailSection from '@/components/school-settings/EmailSection.vue';
import ImagesSection from '@/components/school-settings/ImagesSection.vue';
import LoginPageSection from '@/components/school-settings/LoginPageSection.vue';
import LandingPageSection from '@/components/school-settings/LandingPageSection.vue';
import Form29Section from '@/components/school-settings/Form29Section.vue';
import { apiRequest } from '@/api/http';
import {
  SCHOOL_SETTINGS_KEY,
  useSchoolSettingsForm,
} from '@/composables/useSchoolSettingsForm';
import { useSessionGuard } from '@/composables/useSessionGuard';

// School-wide identity used by the UI, emails and DepEd forms. Laid out
// like the starter kit's settings pages: a section nav on the left and one
// section at a time on the right (kept in ?section= for reload/links).
useSessionGuard();

const SECTIONS = [
  { id: 'identity', title: 'General', icon: 'mdi-school-outline', component: IdentitySection },
  { id: 'deped', title: 'DepEd details', icon: 'mdi-file-document-outline', component: DepedSection },
  { id: 'form29', title: 'Form 29 signatories', icon: 'mdi-draw-pen', component: Form29Section },
  { id: 'email', title: 'Email', icon: 'mdi-email-outline', component: EmailSection },
  { id: 'images', title: 'Images', icon: 'mdi-image-outline', component: ImagesSection },
  { id: 'login', title: 'Sign-in page', icon: 'mdi-login-variant', component: LoginPageSection },
  { id: 'landing', title: 'Landing page', icon: 'mdi-home-outline', component: LandingPageSection },
];

const route = useRoute();
const active = computed(
  () => SECTIONS.find((s) => s.id === route.query.section) ?? SECTIONS[0],
);
const navItems = computed(() =>
  SECTIONS.map((s) => ({ ...s, to: { query: { ...route.query, section: s.id } } })),
);

// One shared form for all sections.
provide(SCHOOL_SETTINGS_KEY, useSchoolSettingsForm());

// Only users whose current role includes the School Settings page may edit
// (the server enforces the same rule on save).
const access = shallowRef('checking'); // 'checking' | 'allowed' | 'denied'
apiRequest('get', '/assigned-modules/getMyAssignedModules/my')
  .then((mod) => {
    let pages = [];
    try {
      pages = JSON.parse(mod?.assign_mods || '[]');
    } catch {
      pages = [];
    }
    const routes = pages.flatMap((p) => [p.route, ...(p.subLink || []).map((s) => s.route)]);
    access.value = routes.includes('/school-settings') ? 'allowed' : 'denied';
  })
  .catch(() => {
    access.value = 'denied';
  });
</script>

<template>
  <div v-if="access === 'checking'" class="d-flex justify-center pa-8">
    <v-progress-circular indeterminate size="28" width="3" />
  </div>

  <UiCard
    v-else-if="access === 'denied'"
    title="You don't have access"
    description="School branding can only be changed by an account whose role includes School Settings, such as the superadmin."
  />

  <SettingsLayout
    v-else
    title="School settings"
    description="Manage how your school appears in the app, on emails and on printed forms."
    :items="navItems"
    :active-id="active.id"
  >
    <component :is="active.component" />
  </SettingsLayout>
</template>
